import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  fetchKbArticlesList,
  deleteKbArticleById,
  toggleKbArticleStatus,
} from "../../services/axios/apiManager";
import BSPagination from "../../Components/pagination";
import LoadingSpinner from "../../Components/spinner";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import $ from "jquery";
import "datatables.net-bs5";
import DashboardLayout from "../DashboardLayout";
import AddKbCategoryModal from "./addKbCategoryModal";

const KbArticlesList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [modalOptions, setModalOptions] = useState({});
  const [showCatModal, setShowCatModal] = useState(false);
  const tableRef = useRef(null);

  const fetchArticles = async (page = 1) => {
    setLoading(true);
    const response = await fetchKbArticlesList(page, pagination.pageSize || 10);
    if (response?.data?.success) {
      setArticles(response.data.data);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
        pageSize: response.data.pagination.pageSize || prev.pageSize,
      }));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handlePageChange = (page) => {
    fetchArticles(page);
  };

  const handleOpenAddEditPage = (article = {}) => {
    if (article?.id) {
      navigate(`/KbManagement/edit/${article.id}`);
    } else {
      navigate("/KbManagement/add");
    }
  };

  const handleToggleStatus = (article) => {
    setModalOptions({
      header: "Change Status",
      body: `Are you sure you want to ${article.status === 1 ? "deactivate" : "activate"} "${article.title}"?`,
      okay: "Confirm",
      method: async () => {
        const response = await toggleKbArticleStatus({
          id: article.id,
          status: article.status === 1 ? 0 : 1,
        });
        if (response?.data?.success) {
          toast.success("Status updated");
          fetchArticles(pagination.currentPage);
        }
        setShowOptionsModal(false);
      },
    });
    setShowOptionsModal(true);
  };

  const handleDelete = (article) => {
    setModalOptions({
      header: "Delete Article",
      body: `Are you sure you want to delete "${article.title}"?`,
      okay: "Delete",
      method: async () => {
        const response = await deleteKbArticleById(article.id);
        if (response?.data?.success) {
          toast.success("Article deleted");
          fetchArticles(pagination.currentPage);
        }
        setShowOptionsModal(false);
      },
    });
    setShowOptionsModal(true);
  };

  useEffect(() => {
    const rows = articles.map((a) => [
      a.title,
      a.kb_category?.name || "Uncategorized",
      a.kb_creator
        ? `${a.kb_creator.first_name} ${a.kb_creator.last_name}`
        : "N/A",
      a.views || 0,
      a.is_featured === 1 ? "Yes" : "No",
      a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "N/A",
      a.status === 1 ? "Active" : "Inactive",
      "", // Actions
    ]);

    const config = {
      data: rows,
      columns: [
        { title: "Title" },
        { title: "Category" },
        { title: "Author" },
        { title: "Views" },
        { title: "Featured" },
        { title: "Date" },
        { title: "Status" },
        { title: "Actions", orderable: false },
      ],
      destroy: true,
      paging: false,
      searching: false,
      info: false,
      responsive: true,
      createdRow: (row, data, idx) => {
        const article = articles[idx];
        const $row = $("td", row);

        // Featured status color
        $row
          .eq(4)
          .addClass(article.is_featured === 1 ? "text-success" : "text-muted");

        // Status toggle
        const $status = $(
          `<button class="btn btn-link p-0 ${article.status === 1 ? "text-success" : "text-warning"}">${data[6]}</button>`,
        ).on("click", () => handleToggleStatus(article));
        $row.eq(6).empty().append($status);

        // Actions
        const $act = $row.eq(7).empty().css("display", "flex");
        $act.append(
          $(
            `<button class="btn btn-outline-primary btn-sm me-1"><i class="fa fa-edit"></i></button>`,
          ).on("click", () => handleOpenAddEditPage(article)),
        );

        $act.append(
          $(
            `<button class="btn btn-outline-danger btn-sm"><i class="fa fa-trash"></i></button>`,
          ).on("click", () => handleDelete(article)),
        );
      },
    };

    const $tbl = $(tableRef.current);
    if ($.fn.dataTable.isDataTable($tbl)) {
      $tbl.DataTable().clear().rows.add(rows).draw();
    } else {
      $tbl.DataTable(config);
    }
    return () => {
      if ($.fn.dataTable.isDataTable($tbl)) $tbl.DataTable().destroy();
    };
  }, [articles]);

  return (
    <DashboardLayout>
      <div className="row" data-testid="KbManagement">
        <div className="col-12 mb-3">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <a>Management</a>
                </li>
                <li
                  className="breadcrumb-item active"
                  style={{ color: "#d1cdcdbf" }}
                >
                  Knowledgebase
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="container text-light bg-dark rounded mb-4 p-4 shadow">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="text-success">Knowledge Base Articles</h3>
          <div>
            <Button
              variant="outline-success"
              className="me-2"
              onClick={() => navigate("/KbManagement/categories")}
            >
              Article Categories
            </Button>
            <Button variant="success" onClick={() => handleOpenAddEditPage({})}>
              Add New Article
            </Button>
          </div>
        </div>

        <div className="position-relative">
          {loading && (
            <div
              className="position-absolute top-50 start-50 translate-middle"
              style={{ zIndex: 10 }}
            >
              <LoadingSpinner size="lg" />
            </div>
          )}

          <div className={`table-responsive ${loading ? "opacity-50" : ""}`}>
            <table
              ref={tableRef}
              className="table table-dark table-striped w-100"
            />

            <div className="d-flex justify-content-end mt-4">
              <BSPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>

        <Modal
          show={showOptionsModal}
          onHide={() => setShowOptionsModal(false)}
          centered
        >
          <Modal.Header closeButton className="bg-dark text-light border-0">
            <Modal.Title>{modalOptions.header}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark text-light">
            {modalOptions.body}
          </Modal.Body>
          <Modal.Footer className="bg-dark text-light border-0">
            <Button
              variant="secondary"
              onClick={() => setShowOptionsModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={modalOptions.method}>
              {modalOptions.okay}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default KbArticlesList;
